import React from "react";

/**
 * Global ErrorBoundary — catches any uncaught render error in the React tree
 * and shows a friendly fallback instead of a blank screen.
 *
 * Without this, a single bad render anywhere (e.g. accidentally passing an
 * object/array as a React child) blanks the entire app — exactly the
 * "blank screen on /astro-vastu-report" symptom we hit in production.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, errorMessage: "" };
  }

  static getDerivedStateFromError(error) {
    let msg = "Something went wrong while rendering this page.";
    try {
      if (error?.message) msg = String(error.message).slice(0, 240);
    } catch { /* noop */ }
    return { hasError: true, errorMessage: msg };
  }

  componentDidCatch(error, info) {
    // Surface for debugging via DevTools — production builds still log this.
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary]", error, info?.componentStack);
  }

  handleReload = () => {
    try { window.location.reload(); } catch { /* noop */ }
  };

  handleHome = () => {
    try { window.location.href = "/"; } catch { /* noop */ }
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div
        data-testid="error-boundary-fallback"
        style={{
          minHeight: "60vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "32px 20px",
          fontFamily: "-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif",
        }}
      >
        <div style={{ maxWidth: 460, textAlign: "center" }}>
          <div style={{
            width: 64, height: 64, borderRadius: 16,
            background: "linear-gradient(135deg,#FDF2F8,#FEE2E2)",
            color: "#B91C1C", fontSize: 30, lineHeight: "64px",
            margin: "0 auto 18px", fontWeight: 700,
          }}>!</div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: "#1a1a1a", margin: "0 0 8px" }}>
            Something went wrong
          </h1>
          <p style={{ color: "#666", fontSize: 14, lineHeight: 1.6, margin: "0 0 20px" }}>
            We hit an unexpected hiccup loading this page. Refresh to try again, or head back to the dashboard.
          </p>
          {this.state.errorMessage && (
            <p style={{
              fontFamily: "monospace", fontSize: 11, color: "#999",
              background: "#fafafa", padding: "8px 12px", borderRadius: 6,
              margin: "0 0 20px", wordBreak: "break-word",
            }}>
              {this.state.errorMessage}
            </p>
          )}
          <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
            <button
              onClick={this.handleReload}
              data-testid="error-boundary-reload"
              style={{
                padding: "10px 18px", fontSize: 14, fontWeight: 600,
                background: "#1a1a1a", color: "#fff", border: 0,
                borderRadius: 999, cursor: "pointer",
              }}
            >
              Refresh page
            </button>
            <button
              onClick={this.handleHome}
              data-testid="error-boundary-home"
              style={{
                padding: "10px 18px", fontSize: 14, fontWeight: 600,
                background: "#fff", color: "#1a1a1a",
                border: "1px solid #e5e5e5", borderRadius: 999, cursor: "pointer",
              }}
            >
              Go home
            </button>
          </div>
        </div>
      </div>
    );
  }
}
